/* RESPONSIBLE TEAM: team-knowledge-foundations */
import { EntityType } from 'embercom/models/data/entity-types';
import { CAN_MANAGE_ARTICLES_PERMISSION } from 'embercom/lib/articles/constants';
import { CAN_MANAGE_KNOWLEDGE_BASE_CONTENT } from 'embercom/lib/knowledge-hub/constants';
import type IntlService from 'ember-intl/services/intl';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type ContentFragment from 'embercom/models/content-service/content-fragment';
import { FIN_SUPPORTED_LANGUAGES, type Language } from 'embercom/lib/ai-content-library/constants';
import { ARTICLES_FEATURE, MULTILINGUAL_ARTICLES_FEATURE } from 'embercom/lib/billing';
import type KnowledgeHubContentWrapper from 'embercom/models/content-service/knowledge-hub-content-wrapper';
import { ContentWrapperType } from 'embercom/models/content-service/content-wrapper';

export const USER_ROLE = 'user_role';
export const VISITOR_ROLE = 'visitor_role';
export const LEAD_ROLE = 'contact_role';
export type HelpCenterAudienceRole = typeof USER_ROLE | typeof VISITOR_ROLE | typeof LEAD_ROLE;

export interface ContentFragmentTuple {
  entity_id: number;
  entity_type: number;
}

export interface BulkActionApiParams
  extends AudienceBulkActionApiParams, HelpCenterAudienceBulkActionApiParams {
  app_id: number;
  is_bulk_selection: boolean;
  contents_to_update: ContentFragmentTuple[] | null;
  unselected_tuples: ContentFragmentTuple[] | null;
  query: any;
  tag_ids_to_apply?: string[];
  tag_ids_to_remove?: string[];
  language_code?: string;
  destination_folder_id?: number | string;
  author_id?: string;
  collection_id?: string;
  should_affect_sub_folder_items?: boolean;
  agent_types?: string[];
  availability_action?: string;
  scheduled_at?: string;
  timezone?: string;
  revert_at?: string;
  content_update_type?: string;
}

export interface AudienceBulkActionApiParams {
  segment_ids_to_add?: number[] | null;
  segment_ids_to_delete?: number[] | null;
  remove_all_segments?: boolean;
}

export interface HelpCenterAudienceBulkActionApiParams {
  roles_to_add?: HelpCenterAudienceRole[];
  roles_to_remove?: HelpCenterAudienceRole[];
}

export enum ActionName {
  ArticlePublish = 'article-publish',
  ArticleUnpublish = 'article-unpublish',
  AICopilotEnable = 'ai-copilot-enable',
  AICopilotDisable = 'ai-copilot-disable',
  AIAgentEnable = 'ai-agent-enable',
  AIAgentDisable = 'ai-agent-disable',
  AISalesAgentEnable = 'ai-sales-agent-enable',
  AISalesAgentDisable = 'ai-sales-agent-disable',
  AIEcommerceEnable = 'ai-ecommerce-enable',
  AIEcommerceDisable = 'ai-ecommerce-disable',
  UpdateLanguage = 'update-language',
  MoveToFolder = 'move-to-folder',
  UpdateTags = 'update-tags',
  UpdateAudience = 'update-audience',
  UpdateHelpCenterAudience = 'update-help-center-audience',
  Delete = 'delete',
  UpdateAuthor = 'update-author',
  MoveToCollection = 'move-to-collection',
  ScheduleAgent = 'schedule-agent',
  ClearScheduleAgent = 'clear-schedule-agent',
  SchedulePublication = 'schedule-publication',
  ClearSchedulePublication = 'clear-schedule-publication',
}

export const actionParameters: {
  [key in ActionName]: {
    endpoint: string;
    applicableContentTypes?: number[];
    requiredPermission: string;
  };
} = {
  [ActionName.ArticlePublish]: {
    endpoint: 'bulk_publish',
    applicableContentTypes: [EntityType.ArticleContent],
    requiredPermission: CAN_MANAGE_ARTICLES_PERMISSION,
  },
  [ActionName.ArticleUnpublish]: {
    endpoint: 'bulk_unpublish',
    applicableContentTypes: [EntityType.ArticleContent],
    requiredPermission: CAN_MANAGE_ARTICLES_PERMISSION,
  },
  [ActionName.AICopilotEnable]: {
    endpoint: 'bulk_enable_copilot_availability',
    requiredPermission: CAN_MANAGE_KNOWLEDGE_BASE_CONTENT,
    applicableContentTypes: [
      EntityType.ArticleContent,
      EntityType.InternalArticle,
      EntityType.ExternalContent,
      EntityType.ContentSnippet,
      EntityType.FileSourceContent,
      EntityType.EcommerceProduct,
    ],
  },
  [ActionName.AICopilotDisable]: {
    endpoint: 'bulk_disable_copilot_availability',
    requiredPermission: CAN_MANAGE_KNOWLEDGE_BASE_CONTENT,
    applicableContentTypes: [
      EntityType.ArticleContent,
      EntityType.InternalArticle,
      EntityType.ExternalContent,
      EntityType.ContentSnippet,
      EntityType.FileSourceContent,
      EntityType.EcommerceProduct,
    ],
  },
  [ActionName.AIAgentEnable]: {
    endpoint: 'bulk_enable_chatbot_availability',
    requiredPermission: CAN_MANAGE_KNOWLEDGE_BASE_CONTENT,
    applicableContentTypes: [
      EntityType.ArticleContent,
      EntityType.ExternalContent,
      EntityType.ContentSnippet,
      EntityType.FileSourceContent,
      EntityType.EcommerceProduct,
    ],
  },
  [ActionName.AIAgentDisable]: {
    endpoint: 'bulk_disable_chatbot_availability',
    requiredPermission: CAN_MANAGE_KNOWLEDGE_BASE_CONTENT,
    applicableContentTypes: [
      EntityType.ArticleContent,
      EntityType.ExternalContent,
      EntityType.ContentSnippet,
      EntityType.FileSourceContent,
      EntityType.EcommerceProduct,
    ],
  },
  [ActionName.AISalesAgentEnable]: {
    endpoint: 'bulk_enable_sales_agent_availability',
    requiredPermission: CAN_MANAGE_KNOWLEDGE_BASE_CONTENT,
    applicableContentTypes: [
      EntityType.ArticleContent,
      EntityType.ExternalContent,
      EntityType.ContentSnippet,
      EntityType.FileSourceContent,
    ],
  },
  [ActionName.AISalesAgentDisable]: {
    endpoint: 'bulk_disable_sales_agent_availability',
    requiredPermission: CAN_MANAGE_KNOWLEDGE_BASE_CONTENT,
    applicableContentTypes: [
      EntityType.ArticleContent,
      EntityType.ExternalContent,
      EntityType.ContentSnippet,
      EntityType.FileSourceContent,
    ],
  },
  [ActionName.AIEcommerceEnable]: {
    endpoint: 'bulk_enable_chatbot_availability',
    requiredPermission: CAN_MANAGE_KNOWLEDGE_BASE_CONTENT,
    applicableContentTypes: [
      EntityType.ArticleContent,
      EntityType.ExternalContent,
      EntityType.ContentSnippet,
      EntityType.FileSourceContent,
      EntityType.EcommerceProduct,
    ],
  },
  [ActionName.AIEcommerceDisable]: {
    endpoint: 'bulk_disable_chatbot_availability',
    requiredPermission: CAN_MANAGE_KNOWLEDGE_BASE_CONTENT,
    applicableContentTypes: [
      EntityType.ArticleContent,
      EntityType.ExternalContent,
      EntityType.ContentSnippet,
      EntityType.FileSourceContent,
      EntityType.EcommerceProduct,
    ],
  },
  [ActionName.UpdateLanguage]: {
    endpoint: 'bulk_update_language',
    applicableContentTypes: [
      EntityType.InternalArticle,
      EntityType.ExternalContent,
      EntityType.ContentSnippet,
      EntityType.FileSourceContent,
    ],
    requiredPermission: CAN_MANAGE_KNOWLEDGE_BASE_CONTENT,
  },
  [ActionName.MoveToFolder]: {
    endpoint: 'bulk_move_to_folder',
    applicableContentTypes: [
      EntityType.InternalArticle,
      EntityType.ArticleContent,
      EntityType.ContentSnippet,
      EntityType.FileSourceContent,
      EntityType.ContentLibraryFolder,
    ],
    requiredPermission: CAN_MANAGE_KNOWLEDGE_BASE_CONTENT,
  },
  [ActionName.UpdateTags]: {
    endpoint: 'bulk_update_tags',
    applicableContentTypes: [
      EntityType.ArticleContent,
      EntityType.InternalArticle,
      EntityType.ExternalContent,
      EntityType.ContentSnippet,
      EntityType.FileSourceContent,
      EntityType.EcommerceProduct,
    ],
    requiredPermission: CAN_MANAGE_KNOWLEDGE_BASE_CONTENT,
  },
  [ActionName.UpdateHelpCenterAudience]: {
    endpoint: 'bulk_change_help_center_audience',
    requiredPermission: CAN_MANAGE_ARTICLES_PERMISSION,
    applicableContentTypes: [EntityType.ArticleContent],
  },
  [ActionName.UpdateAudience]: {
    endpoint: 'bulk_change_segment',
    requiredPermission: CAN_MANAGE_KNOWLEDGE_BASE_CONTENT,
    applicableContentTypes: [
      EntityType.ArticleContent,
      EntityType.InternalArticle,
      EntityType.ExternalContent,
      EntityType.ContentSnippet,
      EntityType.FileSourceContent,
      EntityType.EcommerceProduct,
    ],
  },
  [ActionName.Delete]: {
    endpoint: 'bulk_delete',
    requiredPermission: CAN_MANAGE_KNOWLEDGE_BASE_CONTENT,
    applicableContentTypes: [
      EntityType.ArticleContent,
      EntityType.InternalArticle,
      EntityType.ContentSnippet,
      EntityType.FileSourceContent,
      EntityType.ContentLibraryFolder,
    ],
  },
  [ActionName.UpdateAuthor]: {
    endpoint: 'bulk_update_author',
    requiredPermission: CAN_MANAGE_ARTICLES_PERMISSION,
    applicableContentTypes: [EntityType.ArticleContent],
  },
  [ActionName.MoveToCollection]: {
    endpoint: 'bulk_move_to_collection',
    requiredPermission: CAN_MANAGE_ARTICLES_PERMISSION,
    applicableContentTypes: [EntityType.ArticleContent],
  },
  [ActionName.ScheduleAgent]: {
    endpoint: 'bulk_schedule_agent',
    requiredPermission: CAN_MANAGE_KNOWLEDGE_BASE_CONTENT,
    applicableContentTypes: [
      EntityType.ExternalContent,
      EntityType.ContentSnippet,
      EntityType.FileSourceContent,
      EntityType.InternalArticle,
    ],
  },
  [ActionName.ClearScheduleAgent]: {
    endpoint: 'bulk_clear_schedule_agent',
    requiredPermission: CAN_MANAGE_KNOWLEDGE_BASE_CONTENT,
    applicableContentTypes: [
      EntityType.ExternalContent,
      EntityType.ContentSnippet,
      EntityType.FileSourceContent,
      EntityType.InternalArticle,
    ],
  },
  [ActionName.SchedulePublication]: {
    endpoint: 'bulk_schedule_publication',
    requiredPermission: CAN_MANAGE_ARTICLES_PERMISSION,
    applicableContentTypes: [EntityType.ArticleContent],
  },
  [ActionName.ClearSchedulePublication]: {
    endpoint: 'bulk_clear_schedule_publication',
    requiredPermission: CAN_MANAGE_ARTICLES_PERMISSION,
    applicableContentTypes: [EntityType.ArticleContent],
  },
};

function languageOptions(applyLanguage: (locale: string) => void): Array<OptionItem> {
  return FIN_SUPPORTED_LANGUAGES.map((language: Language) => {
    return {
      text: language.name,
      value: language.locale,
      onSelectItem: () => applyLanguage(language.locale),
    };
  });
}

export type BulkActionConfig = {
  id: string;
  label?: string;
  visible: boolean;
  icon?: InterfaceIconName;
  isDropdownOpener?: boolean;
  options: {
    heading?: string;
    items: OptionItem[];
  }[];
};

export interface OptionItem {
  icon?: InterfaceIconName;
  value?: string;
  text?: string;
  tooltipText?: string;
  isDisabled?: boolean;
  onSelectItem?: () => void;
  component?: string;
  groupList?: Array<{ items: OptionItem[] }>;
  componentShouldReplaceItem?: boolean;
  action?: ActionName;
  isDestructive?: boolean;
  selectedFragments?: Set<ContentFragment>;
  applyAudience?: (audienceBulkActionApiParams: AudienceBulkActionApiParams) => void;
  applyHelpCenterAudience?: (
    helpCenterAudienceBulkActionApiParams: HelpCenterAudienceBulkActionApiParams,
  ) => void;
}

const SCHEDULE_TAG_NAME = 'scheduled-for-agent-availability';

export function hasScheduledContentInSelection(
  isBulkSelection: boolean,
  selectedFragments: Set<ContentFragment>,
  totalCount: number,
): boolean {
  if (isBulkSelection && totalCount > selectedFragments.size) {
    return true;
  }
  return Array.from(selectedFragments)
    .filter((fragment) => fragment != null)
    .some((fragment) => {
      // tag_ids are sideloaded on the ContentWrapper, not on the ContentFragment directly
      let wrapperTags = (fragment.contentWrapper as KnowledgeHubContentWrapper)?.tags;
      if (!wrapperTags) {
        return false;
      }
      return wrapperTags.any((tag: any) => tag.name === SCHEDULE_TAG_NAME);
    });
}

export function bulkActions(
  intl: IntlService,
  isBulkSelection: boolean,
  selectedFragments: Set<ContentFragment>,
  openMoveToFolderModal: () => void,
  applyLanguage: (locale: string) => void,
  applyBulkAction: (actionName: ActionName) => void,
  applyAudience: (audienceBulkActionApiParams: AudienceBulkActionApiParams) => void,
  applyHelpCenterAudience: (
    helpCenterAudienceBulkActionApiParams: HelpCenterAudienceBulkActionApiParams,
  ) => void,
  openUpdateTagsModal: () => void,
  openConfirmDeleteModal: () => void,
  openUpdateAuthorModal: () => void,
  openMoveToCollectionModal: () => void,
  openSchedulePublishModal: () => void,
  openClearSchedulePublishModal: () => void,
  openScheduleAgentModal: () => void,
  openClearScheduleAgentModal: () => void,
  app: any,
  totalCount: number,
  onlySyncedFoldersSelected?: boolean,
  canApplyHelpCenterAudience?: boolean,
): BulkActionConfig[] {
  let bulkActions: BulkActionConfig[] = [];

  let translationKeyPrefix = 'knowledge-hub.filterable-list.bulk-actions';

  if (app.canUseStandalone) {
    translationKeyPrefix = `${translationKeyPrefix}.standalone`;
  }

  bulkActions.push({
    id: 'change-ai-agent-state',
    label: intl.t(`${translationKeyPrefix}.dropdown.change-ai-agent-state`),
    icon: 'fin',
    visible: isApplicableForContent(
      ActionName.AIAgentEnable,
      isBulkSelection,
      selectedFragments,
      app,
    ),
    options: [
      {
        items: [
          {
            icon: 'check',
            value: ActionName.AIAgentEnable,
            ...decorateForApplicability(
              intl,
              ActionName.AIAgentEnable,
              intl.t(`${translationKeyPrefix}.ai-agent-enable.label`),
              isBulkSelection,
              selectedFragments,
              app,
            ),
          },
          {
            icon: 'x',
            value: ActionName.AIAgentDisable,
            ...decorateForApplicability(
              intl,
              ActionName.AIAgentDisable,
              intl.t(`${translationKeyPrefix}.ai-agent-disable.label`),
              isBulkSelection,
              selectedFragments,
              app,
            ),
          },
        ],
      },
    ],
  });
  if (!app.canUseStandalone || app.canUseCopilotOnStandalone) {
    bulkActions.push({
      id: 'change-ai-copilot-state',
      label: intl.t(`${translationKeyPrefix}.dropdown.change-ai-copilot-state`),
      icon: 'fin',
      visible: isApplicableForContent(
        ActionName.AICopilotEnable,
        isBulkSelection,
        selectedFragments,
        app,
      ),
      options: [
        {
          items: [
            {
              icon: 'check',
              value: ActionName.AICopilotEnable,
              ...decorateForApplicability(
                intl,
                ActionName.AICopilotEnable,
                intl.t(`${translationKeyPrefix}.ai-copilot-enable.label`),
                isBulkSelection,
                selectedFragments,
                app,
              ),
            },
            {
              icon: 'x',
              value: ActionName.AICopilotDisable,
              ...decorateForApplicability(
                intl,
                ActionName.AICopilotDisable,
                intl.t(`${translationKeyPrefix}.ai-copilot-disable.label`),
                isBulkSelection,
                selectedFragments,
                app,
              ),
            },
          ],
        },
      ],
    });
  }
  if (app.canUseFinSalesAgent) {
    bulkActions.push({
      id: 'change-ai-sales-agent-state',
      label: intl.t(
        app.canUseStandalone
          ? 'knowledge-hub.filterable-list.bulk-actions.standalone.dropdown.change-ai-sales-agent-state'
          : 'knowledge-hub.filterable-list.bulk-actions.dropdown.change-ai-sales-agent-state',
      ),
      icon: 'fin',
      visible: isApplicableForContent(
        ActionName.AISalesAgentEnable,
        isBulkSelection,
        selectedFragments,
        app,
      ),
      options: [
        {
          items: [
            {
              icon: 'check',
              value: ActionName.AISalesAgentEnable,
              ...decorateForApplicability(
                intl,
                ActionName.AISalesAgentEnable,
                intl.t(
                  app.canUseStandalone
                    ? 'knowledge-hub.filterable-list.bulk-actions.standalone.ai-sales-agent-enable.label'
                    : 'knowledge-hub.filterable-list.bulk-actions.ai-sales-agent-enable.label',
                ),
                isBulkSelection,
                selectedFragments,
                app,
              ),
            },
            {
              icon: 'x',
              value: ActionName.AISalesAgentDisable,
              ...decorateForApplicability(
                intl,
                ActionName.AISalesAgentDisable,
                intl.t(
                  app.canUseStandalone
                    ? 'knowledge-hub.filterable-list.bulk-actions.standalone.ai-sales-agent-disable.label'
                    : 'knowledge-hub.filterable-list.bulk-actions.ai-sales-agent-disable.label',
                ),
                isBulkSelection,
                selectedFragments,
                app,
              ),
            },
          ],
        },
      ],
    });
  }
  if (app.canUseFeature('fin-ecommerce-agent')) {
    bulkActions.push({
      id: 'change-ai-ecommerce-state',
      label: intl.t(
        'knowledge-hub.filterable-list.bulk-actions.dropdown.change-ai-ecommerce-state',
      ),
      icon: 'fin',
      visible: isApplicableForContent(
        ActionName.AIEcommerceEnable,
        isBulkSelection,
        selectedFragments,
        app,
      ),
      options: [
        {
          items: [
            {
              icon: 'check',
              value: ActionName.AIEcommerceEnable,
              ...decorateForApplicability(
                intl,
                ActionName.AIEcommerceEnable,
                intl.t('knowledge-hub.filterable-list.bulk-actions.ai-ecommerce-enable.label'),
                isBulkSelection,
                selectedFragments,
                app,
              ),
            },
            {
              icon: 'x',
              value: ActionName.AIEcommerceDisable,
              ...decorateForApplicability(
                intl,
                ActionName.AIEcommerceDisable,
                intl.t('knowledge-hub.filterable-list.bulk-actions.ai-ecommerce-disable.label'),
                isBulkSelection,
                selectedFragments,
                app,
              ),
            },
          ],
        },
      ],
    });
  }
  let allContentTypesActions: OptionItem[] = [
    {
      action: ActionName.UpdateTags,
      icon: 'tag',
      ...decorateForApplicability(
        intl,
        ActionName.UpdateTags,
        intl.t('knowledge-hub.filterable-list.bulk-actions.update-tags.label'),
        isBulkSelection,
        selectedFragments,
        app,
      ),
      onSelectItem: () => openUpdateTagsModal(),
    },
    {
      action: ActionName.UpdateAudience,
      component: 'knowledge-hub/filterable-list/bulk-actions/audience-dropdown',
      ...decorateForApplicability(
        intl,
        ActionName.UpdateAudience,
        intl.t(
          app.canUseStandalone
            ? 'knowledge-hub.filterable-list.bulk-actions.audience-dropdown.standalone.change-audience'
            : 'knowledge-hub.filterable-list.bulk-actions.audience-dropdown.change-audience',
        ),
        isBulkSelection,
        selectedFragments,
        app,
      ),
      onSelectItem: () => {},
      componentShouldReplaceItem: true,
      selectedFragments,
      applyAudience,
    },
    {
      action: ActionName.UpdateLanguage,
      component: 'knowledge-hub/filterable-list/bulk-actions/sub-dropdown',
      groupList: [
        {
          items: languageOptions(applyLanguage),
        },
      ],
      icon: 'language',
      ...decorateForApplicability(
        intl,
        ActionName.UpdateLanguage,
        intl.t('knowledge-hub.filterable-list.bulk-actions.dropdown.change-language'),
        isBulkSelection,
        selectedFragments,
        app,
      ),
      onSelectItem: () => {},
      componentShouldReplaceItem: true,
    },
    {
      action: ActionName.MoveToFolder,
      icon: 'move-folder',
      ...(onlySyncedFoldersSelected
        ? {
            text: intl.t('knowledge-hub.filterable-list.bulk-actions.move-to-folder.label'),
            tooltipText: intl.t(
              'knowledge-hub.filterable-list.bulk-actions.move-to-folder.disabled',
            ),
          }
        : decorateForApplicability(
            intl,
            ActionName.MoveToFolder,
            intl.t('knowledge-hub.filterable-list.bulk-actions.move-to-folder.label'),
            isBulkSelection,
            selectedFragments,
            app,
          )),
      isDisabled: onlySyncedFoldersSelected ?? true,
      onSelectItem: () => openMoveToFolderModal(),
    },
    {
      action: ActionName.Delete,
      icon: 'trash',
      isDestructive: true,
      ...decorateForApplicability(
        intl,
        ActionName.Delete,
        intl.t('knowledge-hub.filterable-list.bulk-actions.delete.label'),
        isBulkSelection,
        selectedFragments,
        app,
      ),
      onSelectItem: () => openConfirmDeleteModal(),
    },
  ];

  if (app.canUseFeature('team-knowledge-foundations-fin-availability-scheduling')) {
    let deleteIndex = allContentTypesActions.findIndex((item) => item.action === ActionName.Delete);
    allContentTypesActions.splice(deleteIndex, 0, {
      action: ActionName.ScheduleAgent,
      icon: 'clock',
      ...decorateForApplicability(
        intl,
        ActionName.ScheduleAgent,
        app.canUseStandalone
          ? intl.t('knowledge-hub.filterable-list.bulk-actions.standalone.schedule-agent.label')
          : intl.t('knowledge-hub.filterable-list.bulk-actions.schedule-agent.label'),
        isBulkSelection,
        selectedFragments,
        app,
      ),
      onSelectItem: () => openScheduleAgentModal(),
    });

    if (hasScheduledContentInSelection(isBulkSelection, selectedFragments, totalCount)) {
      let scheduleIndex = allContentTypesActions.findIndex(
        (item) => item.action === ActionName.ScheduleAgent,
      );
      allContentTypesActions.splice(scheduleIndex + 1, 0, {
        action: ActionName.ClearScheduleAgent,
        icon: 'dismiss',
        ...decorateForApplicability(
          intl,
          ActionName.ClearScheduleAgent,
          app.canUseStandalone
            ? intl.t(
                'knowledge-hub.filterable-list.bulk-actions.standalone.clear-schedule-agent.label',
              )
            : intl.t('knowledge-hub.filterable-list.bulk-actions.clear-schedule-agent.label'),
          isBulkSelection,
          selectedFragments,
          app,
        ),
        onSelectItem: () => openClearScheduleAgentModal(),
      });
    }
  }

  if (app.canUseStandalone) {
    allContentTypesActions = allContentTypesActions.filter(
      (action) => action.action !== ActionName.MoveToFolder,
    );
  }

  let publicArticleItems: OptionItem[] = [
    {
      action: ActionName.ArticlePublish,
      component: 'knowledge-hub/filterable-list/bulk-actions/sub-dropdown',
      icon: 'article',
      ...decorateForApplicability(
        intl,
        ActionName.ArticlePublish,
        intl.t('knowledge-hub.filterable-list.bulk-actions.dropdown.change-hc-state'),
        isBulkSelection,
        selectedFragments,
        app,
      ),
      onSelectItem: () => {},
      componentShouldReplaceItem: true,
      groupList: [
        {
          items: [
            {
              value: ActionName.ArticleUnpublish,
              text: intl.t('knowledge-hub.filterable-list.bulk-actions.article-unpublish.label'),
              onSelectItem: () => applyBulkAction(ActionName.ArticleUnpublish),
            },
            {
              value: ActionName.ArticlePublish,
              text: intl.t('knowledge-hub.filterable-list.bulk-actions.article-publish.label'),
              onSelectItem: () => applyBulkAction(ActionName.ArticlePublish),
            },
          ],
        },
      ],
    },
    ...(app.canUseScheduledPublishing
      ? [
          {
            action: ActionName.SchedulePublication,
            component: 'knowledge-hub/filterable-list/bulk-actions/sub-dropdown',
            icon: 'calendar' as InterfaceIconName,
            text: intl.t('knowledge-hub.filterable-list.bulk-actions.schedule-publish.label'),
            onSelectItem: () => {},
            componentShouldReplaceItem: true,
            groupList: [
              {
                items: [
                  {
                    value: 'schedule-publish',
                    text: intl.t(
                      'knowledge-hub.filterable-list.bulk-actions.schedule-publish.set-schedule',
                    ),
                    onSelectItem: () => {
                      document.body.click();
                      openSchedulePublishModal();
                    },
                  },
                  {
                    value: 'clear-schedule-publish',
                    text: intl.t(
                      'knowledge-hub.filterable-list.bulk-actions.schedule-publish.clear-schedule',
                    ),
                    onSelectItem: () => {
                      document.body.click();
                      openClearSchedulePublishModal();
                    },
                  },
                ],
              },
            ],
          },
        ]
      : []),
    {
      action: ActionName.UpdateAuthor,
      icon: 'person-square-fill',
      ...decorateForApplicability(
        intl,
        ActionName.UpdateAuthor,
        intl.t('knowledge-hub.filterable-list.bulk-actions.update-author.label'),
        isBulkSelection,
        selectedFragments,
        app,
      ),
      onSelectItem: () => openUpdateAuthorModal(),
    },
    {
      action: ActionName.MoveToCollection,
      icon: 'move-folder',
      ...decorateForApplicability(
        intl,
        ActionName.MoveToCollection,
        intl.t('knowledge-hub.filterable-list.bulk-actions.move-to-collection.label'),
        isBulkSelection,
        selectedFragments,
        app,
      ),
      onSelectItem: () => openMoveToCollectionModal(),
    },
    ...(app.canUseArticleAudienceTargeting && canApplyHelpCenterAudience
      ? [
          {
            action: ActionName.UpdateHelpCenterAudience,
            component: 'knowledge-hub/filterable-list/bulk-actions/help-center-audience-dropdown',
            ...decorateForApplicability(
              intl,
              ActionName.UpdateHelpCenterAudience,
              intl.t(
                'knowledge-hub.filterable-list.bulk-actions.help-center-audience-dropdown.change-help-center-audience',
              ),
              isBulkSelection,
              selectedFragments,
              app,
            ),
            onSelectItem: () => {},
            componentShouldReplaceItem: true,
            selectedFragments,
            applyHelpCenterAudience,
          },
        ]
      : []),
  ];

  let moreOptions: {
    heading?: string;
    items: OptionItem[];
  }[] = [
    {
      heading: intl.t('knowledge-hub.filterable-list.bulk-actions.all-content-types'),
      items: allContentTypesActions.filter((item) => {
        if (item.action) {
          return isApplicableForContent(item.action, isBulkSelection, selectedFragments, app);
        }
        return false;
      }),
    },
  ];

  let filteredPublicArticleItems = publicArticleItems.filter((item) => {
    if (app.canUseStandalone) {
      return false;
    }
    if (item.action) {
      return isApplicableForContent(item.action, isBulkSelection, selectedFragments, app);
    }
    return false;
  });

  if (filteredPublicArticleItems.length > 0) {
    moreOptions.push({
      heading: intl.t('knowledge-hub.filterable-list.bulk-actions.public-article'),
      items: filteredPublicArticleItems,
    });
  }

  bulkActions.push({
    id: 'more',
    isDropdownOpener: true,
    label: intl.t('knowledge-hub.filterable-list.bulk-actions.dropdown.actions'),
    visible: true,
    options: moreOptions,
  });

  return bulkActions;
}

export function onlyFolderActionApplicable(
  selectedFragments: Set<ContentFragment>,
  contentTypeFilter?: Array<EntityType>,
) {
  if (listIsFilteredByOnlyFolder(contentTypeFilter)) {
    return true;
  }

  let contentTypes = selectedContentTypes(selectedFragments);
  if (contentTypes.size === 1 && contentTypes.has(EntityType.ContentLibraryFolder)) {
    return true;
  }
  return false;
}

function selectedContentTypes(selectedFragments: Set<ContentFragment>): Set<EntityType> {
  let selectedContentTypes = Array.from(selectedFragments)
    .filter((fragment) => fragment != null)
    .map((fragment) => fragment.contentType);
  return new Set<EntityType>(selectedContentTypes);
}

function listIsFilteredByOnlyFolder(contentTypeFilter?: Array<EntityType>) {
  if (contentTypeFilter?.length === 1 && contentTypeFilter[0] === EntityType.ContentLibraryFolder) {
    return true;
  }
  return false;
}

export function resolveApplicableTypes(actionName: ActionName, app: any): number[] | undefined {
  let applicableContentTypes = actionParameters[actionName].applicableContentTypes;
  if (!applicableContentTypes) {
    return undefined;
  }
  if (!app.canUseAudienceForCopilot && actionName === ActionName.UpdateAudience) {
    applicableContentTypes = applicableContentTypes.filter(
      (contentType) => contentType !== EntityType.InternalArticle,
    );
  }
  if (
    (actionName === ActionName.AIAgentEnable || actionName === ActionName.AIAgentDisable) &&
    app.canEnableFinForInternalArticles
  ) {
    applicableContentTypes = [...applicableContentTypes, EntityType.InternalArticle];
  }
  return applicableContentTypes;
}

export function applicableCountForSelection(
  actionName: ActionName,
  selectedFragments: Set<ContentFragment>,
  app: any,
): { applicable: number; total: number; isMixed: boolean } {
  let allowlist = resolveApplicableTypes(actionName, app);
  let total = Array.from(selectedFragments).filter((fragment) => fragment != null).length;
  if (!allowlist || total === 0) {
    return { applicable: total, total, isMixed: false };
  }
  let applicable = Array.from(selectedFragments)
    .filter((fragment) => fragment != null)
    .filter((fragment) => allowlist.includes(fragment.contentType)).length;
  return { applicable, total, isMixed: applicable > 0 && applicable < total };
}

function decorateForApplicability(
  intl: IntlService,
  actionName: ActionName,
  label: string,
  isBulkSelection: boolean,
  selectedFragments: Set<ContentFragment>,
  app: any,
): { text: string; tooltipText?: string } {
  if (isBulkSelection) {
    return { text: label };
  }
  let { applicable, total, isMixed } = applicableCountForSelection(
    actionName,
    selectedFragments,
    app,
  );
  if (!isMixed) {
    return { text: label };
  }
  return {
    text: intl.t('knowledge-hub.filterable-list.bulk-actions.partial-applicable-label', {
      label,
      applicable,
      total,
    }),
    tooltipText: intl.t('knowledge-hub.filterable-list.bulk-actions.partial-applicable-tooltip', {
      skipped: total - applicable,
    }),
  };
}

function isApplicableForContent(
  actionName: ActionName,
  isBulkSelection: boolean,
  selectedFragments: Set<ContentFragment>,
  app: any,
): boolean {
  let applicableContentTypes = resolveApplicableTypes(actionName, app);
  if (!applicableContentTypes || isBulkSelection) {
    return true;
  }

  let selectedContentTypes = Array.from(selectedFragments)
    .filter((fragment) => fragment != null)
    .map((fragment) => fragment.contentType);

  if (actionName === ActionName.ArticlePublish) {
    if (!app.canUseFeature(ARTICLES_FEATURE)) {
      return false;
    } else if (!app.canUseFeature(MULTILINGUAL_ARTICLES_FEATURE)) {
      return Array.from(selectedFragments)
        .filter((fragment) => fragment != null)
        .any((fragment: ContentFragment) => {
          let contentWrapper = fragment.contentWrapper as KnowledgeHubContentWrapper;
          let isDefaultLanguageSelected =
            fragment.contentId === contentWrapper?.defaultContent.contentId;
          return isDefaultLanguageSelected;
        });
    }
  }
  return selectedContentTypes.any((contentType) => applicableContentTypes.includes(contentType));
}

export function bulkActionParamsWithFolderId(
  entityType: EntityType,
  folderIds: string[],
  app_id: number,
): BulkActionApiParams {
  let params: BulkActionApiParams = {
    app_id,
    is_bulk_selection: true,
    contents_to_update: null,
    unselected_tuples: [],
    query: null,
  };
  params.query = {
    add_highlight: false,
    additional_searchable_data: {},
    app_id,
    content_wrapper_types: [ContentWrapperType.SUPPORT_CONTENT],
    folder_entity_type: EntityType.ContentLibraryFolder,
    folder_ids: folderIds,
    object_types: [entityType],
    sort_by: 'title',
    sort_direction: 'asc',
  };
  delete params.query.folder_filter_option;

  return params;
}

export function bulkActionParams(entityType: EntityType, app_id: number): BulkActionApiParams {
  let params: BulkActionApiParams = {
    app_id,
    is_bulk_selection: true,
    contents_to_update: null,
    unselected_tuples: null,
    query: null,
  };
  params.query = {
    add_highlight: false,
    app_id,
    content_terms: undefined,
    content_wrapper_types: [ContentWrapperType.SUPPORT_CONTENT],
    object_types: [entityType],
    sort_by: 'title',
    sort_direction: 'asc',
    title: undefined,
  };
  delete params.query.folder_filter_option;

  return params;
}
